import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import BadgeData from 'entity/BadgeData';
import BadgeQuestion from 'entity/BadgeQuestion';
import StandardQuestion from 'entity/StandardQuestion';
import User from 'entity/User';
import {
  BadgeDataValue,
  GetBadgeDataMessage,
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
} from 'messages/websocket.messages';
import BadgingController from 'rest/BadgingController';
import Paging from 'rest/Paging';

import { socialAppActions, socialAppSelectors } from '../../state/app';
import { SceneService } from '../scene.service';
import { WebsocketService } from '../websocket.service';

@Injectable({
  providedIn: 'root',
})
export class BadgingService extends Loggable {
  private store = inject(Store);
  private websocketService = inject(WebsocketService);
  private sceneService = inject(SceneService);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public readonly isBadgeOpen = signal(false);
  public readonly isChatBadge = signal(false);
  public positionToOpenBadge?: { x: number; y: number };
  private badgeUserIdOpen = '';
  private badgeData: BadgeDataValue[] = [];
  public isSelectedInViewport?: boolean;
  public badgeUserIdOpen$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public badgeData$: BehaviorSubject<BadgeDataValue[]> = new BehaviorSubject<BadgeDataValue[]>([]);
  private previewBadgeDataSubject$: BehaviorSubject<BadgeDataValue[]> = new BehaviorSubject<BadgeDataValue[]>([]);
  public previewBadgeData$ = this.previewBadgeDataSubject$.asObservable();
  public badgeUserRingColor$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private badgingController = new BadgingController();
  public badgeNames$: BehaviorSubject<Record<string, string>> = new BehaviorSubject<Record<string, string>>({});

  private badgeDataRequestThrottleTime = 5000;
  private lastBadgeDataRequests: Map<string, number> = new Map<string, number>();
  private badgeDataRequestTimeout?: NodeJS.Timeout;

  public constructor() {
    super();
    this.setDisplayName('BadgingService');
    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    this.store.select(socialAppSelectors.isBadgeOpen).subscribe((badgeUpdate) => {
      this.toggleBadgeOpen(badgeUpdate);
    });
  }

  // Get badge questions for a Scootaverse
  public getBadgeQuestions(socialRoomSetId: string, page?: Paging): Promise<BadgeQuestion[]> {
    return this.badgingController.getBadgeQuestions(socialRoomSetId, page);
  }

  // Get standard badge questions for a Scootaverse
  public getStandardBadgeQuestions(page?: Paging): Promise<StandardQuestion[]> {
    return this.badgingController.getStandardQuestions(page);
  }

  // Get my badge data
  public getMyBadgeData(socialRoomSetId: string, page?: Paging): Promise<BadgeData[]> {
    return this.badgingController.getMyBadgeData(socialRoomSetId, page);
  }

  // Update user badge data
  public updateBadgeData(socialRoomSetId: string, badgeData: BadgeData[]): Promise<BadgeData[]> {
    return this.badgingController.updateBadgeData(socialRoomSetId, badgeData);
  }

  // Save multiple questions
  public saveBadgeQuestions(socialRoomSetId: string, questions: BadgeQuestion[]) {
    return this.badgingController.saveBadgeQuestions(socialRoomSetId, questions);
  }

  /** Open badge controls */
  //Toggle badge open, receiving open status and userId to get information from
  public toggleBadgeOpen(badgeUpdate: {
    open: boolean;
    userId: string;
    isSelectedInViewport?: boolean;
    ringColor?: string;
    position?: { x: number; y: number };
    isChat?: boolean;
  }): void {
    if (badgeUpdate.open && badgeUpdate.userId && badgeUpdate.userId !== '') {
      this.badgeUserIdOpen = badgeUpdate.userId;
      this.badgeUserIdOpen$.next(badgeUpdate.userId);
      this.getUserBadgeData(badgeUpdate.userId);
    } else {
      this.badgeUserIdOpen = '';
      this.badgeUserIdOpen$.next('');
    }
    if (badgeUpdate.ringColor) {
      this.badgeUserRingColor$?.next(badgeUpdate.ringColor);
    } else {
      this.badgeUserRingColor$?.next('');
    }

    if (badgeUpdate.position) {
      this.positionToOpenBadge = badgeUpdate.position;
    } else {
      this.positionToOpenBadge = undefined;
    }

    this.isChatBadge.set(!!badgeUpdate.isChat);
    this.isSelectedInViewport = badgeUpdate.isSelectedInViewport;
    this.isBadgeOpen.set(badgeUpdate.open);
  }

  // Sets only user name when no information is received from the user
  private setBasicsBadgeQuestionsData(): void {
    const userData = this.getUserDataById(this.badgeUserIdOpen);
    const firstName = {
      propertyName: 'First Name',
      value: userData?.firstName ?? '',
      featured: false,
    };
    const lastName = {
      propertyName: 'Last Name',
      value: userData?.lastName ?? '',
      featured: false,
    };
    this.badgeData = [...this.badgeData, firstName, lastName];
  }

  // Get user information for filling the badge when data is not received
  private getUserDataById(userId: string): User | null {
    const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
    const userData = participants.find(({ user }) => user.userId === userId)?.user || null;
    return userData;
  }

  // Request badge information data
  public getUserBadgeData(userId: string) {
    // Skip if no userId provided
    if (!userId) {
      return;
    }

    const now = Date.now();
    const lastRequest = this.lastBadgeDataRequests.get(userId) || 0;

    // If this user's badge data was requested recently, don't request again
    if (now - lastRequest < this.badgeDataRequestThrottleTime) {
      this.debug(`Skipping badge data request for ${userId} (throttled)`);
      return;
    }

    // Update the timestamp for this user
    this.lastBadgeDataRequests.set(userId, now);

    this.badgeData$.next([]);
    this.badgeData = [];
    try {
      const msg: GetBadgeDataMessage = {
        t: OutgoingMessageType.GET_BADGE_DATA,
        userId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to require badge data', e);
      if (this.isBadgeOpen()) {
        // Fill badge information with basic info when error
        this.setBasicsBadgeQuestionsData();
      }
    }
  }

  // Receive badge information from request
  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.BADGE_DATA: {
        this.badgeData$.next([]);
        this.badgeData = [];

        // If the badge is open, fill the badge with basic information when no data is received
        if (msg.data && msg.data.length && msg.userId === this.badgeUserIdOpen) {
          this.badgeData = msg.data;

          const firstName = this.badgeData.find((item) => item.propertyName === 'First Name');
          const lastName = this.badgeData.find((item) => item.propertyName === 'Last Name');
          if (!firstName && !lastName) {
            this.setBasicsBadgeQuestionsData();
          }

          this.badgeData$.next(this.badgeData);
          // Fill badge names with the received data
        } else if (msg.data && msg.data.length) {
          const firstName = msg.data.find((item) => item.propertyName === 'First Name');
          const lastName = msg.data.find((item) => item.propertyName === 'Last Name');
          if (firstName && lastName) {
            const names = this.badgeNames$.value;
            names[msg.userId] = this.formatName(firstName.value, lastName.value);
            this.badgeNames$.next(names);
          }
        }
        return;
      }
    }
  };

  public setPreviewBadgeData(badgeData: BadgeDataValue[]) {
    this.previewBadgeDataSubject$.next(badgeData);
  }

  private formatName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  public clearUserEditBadge(): void {
    this.store.dispatch(socialAppActions.USER_EDIT_BADGE({ userId: '' }));
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    // Clear badge data request timeout
    if (this.badgeDataRequestTimeout) {
      clearTimeout(this.badgeDataRequestTimeout);
    }
  }
}
